/**
 * ⭐ 2026-10-14 (반복 규칙 G · 사용자 확정) — 반복 일정 수정이 '이 일정만'으로 따로 고친 회차에 닿을 때 한 번 묻는다.
 *
 * ```
 * ① 내용 칸 겹침 — 「전체」·「이후」로 바꾸는 칸을 '이 일정만'으로도 고친 회차가 있다
 *    서버 409 RECURRENCE_ALL_OVERRIDE_CONFLICT
 *    → 「기존에 '이 일정만'으로 수정한 내역이 있습니다. 해당 일정도 수정하겠습니까?」
 *        예      ⇒ overrideConflict: 'overwrite' 로 다시 보낸다(그 회차들도 새 값)
 *        아니오  ⇒ overrideConflict: 'keep' 으로 다시 보낸다(그 회차는 그대로, 저장은 된다)
 *        닫기    ⇒ 저장하지 않는다
 * ② 수정 내역 손실 — 「전체」·「이후」의 날짜·시간·반복 변경으로 그 회차가 사라지거나 옮겨지지 못한다 (G 보강)
 *    서버 409 RECURRENCE_OVERRIDES_WILL_BE_LOST
 *    → 같은 질문 + 「예를 누르면 따로 수정한 내용은 새 일정에 맞춰 사라집니다.」
 *        예          ⇒ confirmOverrideLoss: true 로 다시 보낸다
 *        아니오·닫기  ⇒ 저장하지 않는다('유지하고 저장'은 할 수 없다 — 그 회차가 새 일정에 없다)
 * 겹침·손실 없음 → 서버가 묻지 않고 200 — 확인 창도 안 뜬다. 둘 다면 ① → ② 차례로 묻는다.
 * ```
 * ⚠ 다른 오류는 그대로 던진다 — 부르는 쪽 catch 가 사람 말로 알린다.
 */
import { layerChoice, type LayerChoice } from '../../components/LayerPopup/LayerPopup';
import { tCalCreate } from '../../lib/calendar-create-i18n';
import type { PatchEventInput } from '../../api/calendar';

/**
 * 다시 보낼 때 본문에 더하는 결정들. 비어 있으면 처음 요청 그대로.
 * ⚠ 본문(`PatchEventInput`)의 두 칸에서 그대로 따온다 — 두 벌로 적으면 서버 칸이 바뀔 때 한쪽만 고친다.
 */
export type EditConfirms = Pick<PatchEventInput, 'overrideConflict' | 'confirmOverrideLoss'>;

function codeOf409(err: unknown): unknown {
  const res = (err as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
  return res?.status === 409 ? res.data?.code : undefined;
}

/** 서버의 「'이 일정만' 수정 칸과 겹친다」 409 인가. */
export function isOverrideConflictError(err: unknown): boolean {
  return codeOf409(err) === 'RECURRENCE_ALL_OVERRIDE_CONFLICT';
}

/** 서버의 「'이 일정만' 수정 내역이 사라진다」 409 인가. */
export function isOverrideLossError(err: unknown): boolean {
  return codeOf409(err) === 'RECURRENCE_OVERRIDES_WILL_BE_LOST';
}

/**
 * `send({})` 로 보내 보고, 409 면 물어서 결정을 더해 다시 보낸다(각 질문은 한 번씩만).
 * @returns 저장됐으면 true, 사용자가 닫았거나(①) 아니오·닫기(②)면 false.
 */
export async function sendWithEditConfirms(
  send: (confirms: EditConfirms) => Promise<unknown>,
  askConflict: () => Promise<LayerChoice>,
  askLoss: () => Promise<LayerChoice>,
): Promise<boolean> {
  const confirms: EditConfirms = {};
  for (;;) {
    try {
      await send({ ...confirms });
      return true;
    } catch (err) {
      if (isOverrideConflictError(err) && confirms.overrideConflict === undefined) {
        const choice = await askConflict();
        if (choice === null) return false;
        confirms.overrideConflict = choice === 'yes' ? 'overwrite' : 'keep';
        continue;
      }
      if (isOverrideLossError(err) && !confirms.confirmOverrideLoss) {
        if ((await askLoss()) !== 'yes') return false;
        confirms.confirmOverrideLoss = true;
        continue;
      }
      throw err;
    }
  }
}

/** 결정이 있으면 본문에 싣는다 — 없으면 본문 그대로(종전 요청과 같다). */
export function withEditConfirms(body: PatchEventInput, confirms: EditConfirms): PatchEventInput {
  return Object.keys(confirms).length > 0 ? { ...body, ...confirms } : body;
}

/** ① 확인 창 — 예/아니오 + 닫기(×)·ESC·배경 = 저장 안 함(LayerPopup 의 layerChoice). */
export function askOverrideConflict(): Promise<LayerChoice> {
  return layerChoice(tCalCreate('rc.override_conflict_confirm'), {
    confirmText: tCalCreate('rc.override_conflict_yes'),
    cancelText: tCalCreate('rc.override_conflict_no'),
  });
}

/** ② 확인 창 — 같은 질문 + 사라진다는 안내. 예만 진행. */
export function askOverrideLoss(): Promise<LayerChoice> {
  return layerChoice(`${tCalCreate('rc.override_conflict_confirm')}\n${tCalCreate('rc.override_loss_note')}`, {
    confirmText: tCalCreate('rc.override_conflict_yes'),
    cancelText: tCalCreate('rc.override_conflict_no'),
  });
}
